import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { Screen } from "@/components/Screen";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  Smartphone,
  Trash2,
  Send,
  Loader2,
  Settings as SettingsIcon,
  BellOff,
  Clock,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  getNotificationSettings,
  updateNotificationSettings,
  listTripOverrides,
  setTripOverride,
  deleteTripOverride,
  listMyDevices,
  revokeMyDevice,
  sendTestPush,
  type DeviceTokenRow,
  type TripOverrideRow,
} from "@/lib/notifications.functions";
import { CategoryMatrix } from "@/components/notifications/CategoryMatrix";
import { QuietHoursEditor } from "@/components/notifications/QuietHoursEditor";
import { TripOverrideSheet } from "@/components/notifications/TripOverrideSheet";
import {
  DEFAULT_QUIET_HOURS,
  NOTIF_CATEGORIES,
  type NotifMatrix,
  type QuietHours,
} from "@/lib/notifications.types";
import { isNative } from "@/lib/native";
import { ensurePushRegistered } from "@/lib/native/push-register";
import { openAppSettings } from "@/lib/native/open-app-settings";
import { REMINDER_DELAY_OPTIONS, getReminderDelayMs, setReminderDelayMs } from "@/lib/prefs";
import { getDeviceStamp } from "@/lib/native/device-info";
import { useServerFn } from "@/lib/server-fn-shim";
import { getMyTrip } from "@/lib/trip.functions";
import { Inbox, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";


export const Route = createFileRoute("/_authenticated/notifications")({
  head: () =>
    pageMeta({
      title: "Notifications — Roava by WGP Apps",
      description:
        "Choose what Roava sends you, when, and to which devices.",
      path: "/notifications",
    }),
  component: NotificationsPage,
});

type TripRow = { id: string; name: string };

function NotificationsPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();


  // Admin check via getMyTrip (returns isAdmin).
  const tripFn = useServerFn(getMyTrip);
  const tripQ = useQuery({ queryKey: ["my-trip"], queryFn: () => tripFn({}) });

  // ----- Data -----
  const settingsQ = useQuery({
    queryKey: ["notif-settings"],
    queryFn: () => getNotificationSettings(),
  });
  const devicesQ = useQuery({
    queryKey: ["notif-devices"],
    queryFn: () => listMyDevices(),
  });
  const overridesQ = useQuery({
    queryKey: ["notif-trip-overrides"],
    queryFn: () => listTripOverrides(),
  });
  const tripsQ = useQuery({
    queryKey: ["notif-my-trips"],
    queryFn: async (): Promise<TripRow[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase
        .from("trip_members")
        .select("trips(id, name)")
        .eq("user_id", user.id);
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as unknown as Array<{ trips: TripRow | null }>;
      return rows
        .map((r) => r.trips)
        .filter((t): t is TripRow => !!t && !!t.id);
    },
  });

  // ----- Local edit buffer (auto-saved on change with small debounce) -----
  const [matrix, setMatrix] = useState<NotifMatrix>({});
  const [quiet, setQuiet] = useState<QuietHours>(DEFAULT_QUIET_HOURS);
  const hydrated = useRef(false);
  useEffect(() => {
    if (!hydrated.current && settingsQ.data) {
      setMatrix(settingsQ.data.settings ?? {});
      setQuiet(settingsQ.data.quiet_hours ?? DEFAULT_QUIET_HOURS);
      hydrated.current = true;
    }
  }, [settingsQ.data]);

  const saveMut = useMutation({
    mutationFn: (input: { settings?: NotifMatrix; quiet_hours?: QuietHours }) =>
      updateNotificationSettings(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notif-settings"] }),
    onError: (err: any) => toast.error(err?.message ?? t("notif_settings.toast_save_failed")),
  });


  // Debounced auto-save on local edits.
  const saveTimer = useRef<number | null>(null);
  useEffect(() => {
    if (!hydrated.current) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      saveMut.mutate({ settings: matrix, quiet_hours: quiet });
    }, 500);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrix, quiet]);

  // ----- Section: This device -----
  const [enabling, setEnabling] = useState(false);
  const [thisToken, setThisToken] = useState<string | null>(null);

  async function handleEnableThisDevice() {
    setEnabling(true);
    try {
      const r = await ensurePushRegistered();
      if (r.granted && r.registered) {
        toast.success(t("notif_settings.toast_push_enabled"));
        qc.invalidateQueries({ queryKey: ["notif-devices"] });
      } else if (r.granted && !r.registered) {
        toast.error(
          r.reason === "token_timeout"
            ? t("notif_settings.toast_token_timeout")
            : t("notif_settings.toast_save_device_failed", { reason: r.reason ?? t("notif_settings.toast_unknown") }),
        );
        qc.invalidateQueries({ queryKey: ["notif-devices"] });
      } else if (!isNative()) {
        toast.message(t("notif_settings.toast_web_only"));
      } else {
        toast.error(t("notif_settings.toast_notifs_off"));
      }
    } catch (err: any) {
      toast.error(err?.message ?? t("notif_settings.toast_enable_failed"));
    } finally {
      setEnabling(false);
    }
  }


  // Best-effort: detect current device by matching device label.
  const [thisLabel, setThisLabel] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      if (!isNative()) return;
      const s = await getDeviceStamp();
      const label = [s.model, s.osVersion ? `iOS ${s.osVersion}` : null]
        .filter(Boolean)
        .join(" · ");
      setThisLabel(label || null);
    })();
  }, []);

  // ----- Section: Local upload reminder (kept) -----
  const [reminderDelay, setReminderDelayState] = useState<number>(() => getReminderDelayMs());
  function pickReminderDelay(ms: number) {
    setReminderDelayState(ms);
    setReminderDelayMs(ms);
  }

  // ----- Section: Trip overrides -----
  const [editingTrip, setEditingTrip] = useState<TripRow | null>(null);
  const overridesByTrip = useMemo(() => {
    const map = new Map<string, TripOverrideRow>();
    for (const o of overridesQ.data ?? []) map.set(o.trip_id, o);
    return map;
  }, [overridesQ.data]);

  const tripSaveMut = useMutation({
    mutationFn: (input: { trip_id: string; settings: NotifMatrix; muted_until: string | null }) =>
      setTripOverride(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notif-trip-overrides"] }),
    onError: (err: any) => toast.error(err?.message ?? t("notif_settings.toast_save_trip_failed")),
  });
  const tripClearMut = useMutation({
    mutationFn: (trip_id: string) => deleteTripOverride(trip_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notif-trip-overrides"] }),
    onError: (err: any) => toast.error(err?.message ?? t("notif_settings.toast_reset_trip_failed")),
  });

  // ----- Section: Devices -----
  const revokeMut = useMutation({
    mutationFn: (id: string) => revokeMyDevice(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notif-devices"] });
      toast.success(t("notif_settings.toast_push_disabled"));
    },
    onError: (err: any) => toast.error(err?.message ?? t("notif_settings.toast_disable_failed")),
  });

  // ----- Section: Test push -----
  const testMut = useMutation({
    mutationFn: () => sendTestPush(),
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(res.message);
      } else {
        toast.message(res.message);
      }
    },
    onError: (err: any) => toast.error(err?.message ?? t("notif_settings.toast_test_failed")),
  });


  const activeDevices = (devicesQ.data ?? []).filter((d) => !d.revoked_at);
  const revokedDevices = (devicesQ.data ?? []).filter((d) => d.revoked_at);

  return (
    <Screen
      title={t("notif_settings.page_title")}
      subtitle={t("notif_settings.page_subtitle")}
      action={
        <button
          onClick={() => nav({ to: "/permissions" })}
          aria-label={t("notif_settings.back_to_perms")}
          className="grid place-items-center size-9 rounded-full border border-border bg-background hover:bg-accent/40"
        >
          <ChevronLeft className="size-4" />
        </button>
      }
    >
      <Accordion type="multiple" defaultValue={["essentials"]} className="space-y-3">
        <AccordionItem value="essentials" className="rounded-2xl border border-border bg-card overflow-hidden">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex flex-col items-start gap-0.5 text-left">
              <span className="text-sm font-semibold">{t("notif_settings.acc_essentials")}</span>
              <span className="text-[11px] font-normal text-muted-foreground">{tripQ.data?.isAdmin ? t("notif_settings.acc_essentials_sub_admin") : t("notif_settings.acc_essentials_sub")}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 pt-0 space-y-6">
        {/* THIS DEVICE */}
        <section>
          <SectionLabel>{t("notif_settings.sec_this_device")}</SectionLabel>
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-xl bg-accent grid place-items-center text-accent-foreground">
                <Smartphone className="size-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">
                  {thisLabel ?? (isNative() ? t("notif_settings.device_iphone") : t("notif_settings.device_web"))}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isNative()
                    ? activeDevices.length > 0
                      ? t("notif_settings.device_registered", { n: activeDevices.length, count: activeDevices.length })
                      : t("notif_settings.device_not_enabled")
                    : t("notif_settings.device_web_only")}
                </p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                className="rounded-xl"
                disabled={enabling || !isNative()}
                onClick={handleEnableThisDevice}
              >
                {enabling && <Loader2 className="size-3.5 animate-spin mr-1" />}
                {t("notif_settings.btn_enable")}
              </Button>
              {isNative() && (
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-xl gap-1"
                  onClick={() => void openAppSettings()}
                >
                  <SettingsIcon className="size-3.5" /> {t("notif_settings.btn_open_settings")}
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* CATEGORIES — public (released to everyone) */}
        <section>
          <SectionLabel>{t("notif_settings.sec_categories")}</SectionLabel>
          <CategoryMatrix
            value={matrix}
            onChange={setMatrix}
            categoryKeys={NOTIF_CATEGORIES.filter(
              (c) => !c.adminOnly && c.publicChannels.length > 0,
            ).map((c) => c.key)}
          />
        </section>


        {/* ADMIN PREVIEW — only rendered for admins */}
        {tripQ.data?.isAdmin && (
          <section>
            <SectionLabel>
              <span className="inline-flex items-center gap-1.5">
                <Shield className="size-3" />
                {t("notif_settings.sec_admin_preview")}
              </span>
            </SectionLabel>
            <div className="rounded-2xl border border-dashed border-primary/40 bg-primary/[0.03] p-3 space-y-3">
              <p className="text-[11px] text-muted-foreground">
                {t("notif_settings.sec_admin_note")}
              </p>
              {/* In-app channel on public categories */}
              <CategoryMatrix
                value={matrix}
                onChange={setMatrix}
                categoryKeys={NOTIF_CATEGORIES.filter(
                  (c) => !c.adminOnly && c.channels.includes("inapp"),
                ).map((c) => c.key)}
                channelsForCategory={() => ["inapp"]}
              />
              {/* Admin-only categories: show full channel set */}
              <CategoryMatrix
                value={matrix}
                onChange={setMatrix}
                categoryKeys={NOTIF_CATEGORIES.filter((c) => c.adminOnly).map(
                  (c) => c.key,
                )}
                channelsForCategory={(k) =>
                  NOTIF_CATEGORIES.find((c) => c.key === k)?.channels ?? []
                }
              />
              <button
                type="button"
                onClick={() => nav({ to: "/inbox" })}
                className="w-full text-left rounded-2xl border border-border bg-card p-4 hover:bg-accent/30 transition flex items-center gap-3"
              >
                <div className="size-10 rounded-xl bg-accent grid place-items-center text-accent-foreground">
                  <Inbox className="size-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{t("notif_settings.open_inbox_title")}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t("notif_settings.open_inbox_body")}
                  </p>
                </div>
                <ChevronRight className="size-4 text-muted-foreground" />
              </button>
            </div>
          </section>
        )}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="quiet-trips" className="rounded-2xl border border-border bg-card overflow-hidden">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex flex-col items-start gap-0.5 text-left">
              <span className="text-sm font-semibold">{t("notif_settings.acc_quiet_trips")}</span>
              <span className="text-[11px] font-normal text-muted-foreground">{quiet.enabled ? t("notif_settings.quiet_on") : t("notif_settings.quiet_off")} · {t("notif_settings.trips_count", { n: (tripsQ.data ?? []).length, count: (tripsQ.data ?? []).length })}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 pt-0 space-y-6">
        {/* QUIET HOURS */}
        <section>
          <SectionLabel>{t("notif_settings.sec_quiet_hours")}</SectionLabel>
          <QuietHoursEditor value={quiet} onChange={setQuiet} />
        </section>

        {/* PER-TRIP */}
        <section>
          <SectionLabel>{t("notif_settings.sec_per_trip")}</SectionLabel>
          {tripsQ.isLoading ? (
            <div className="rounded-2xl border border-border bg-card p-4 text-xs text-muted-foreground">
              {t("notif_settings.loading_trips")}
            </div>
          ) : (tripsQ.data ?? []).length === 0 ? (
            <div className="rounded-2xl border border-border bg-card p-4 text-xs text-muted-foreground">
              {t("notif_settings.no_trips")}
            </div>
          ) : (
            <div className="space-y-2">
              {(tripsQ.data ?? []).map((trip) => {
                const ov = overridesByTrip.get(trip.id);
                const muted =
                  ov?.muted_until && new Date(ov.muted_until).getTime() > Date.now();
                const overrideCount = ov
                  ? Object.keys(ov.settings ?? {}).length
                  : 0;
                return (
                  <button
                    key={trip.id}
                    type="button"
                    onClick={() => setEditingTrip(trip)}
                    className="w-full text-left rounded-2xl border border-border bg-card p-4 hover:bg-accent/30 transition"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{trip.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                          {muted ? (
                            <>
                              <BellOff className="size-3" />
                              {t("notif_settings.muted_until", { date: new Date(ov!.muted_until!).toLocaleDateString() })}
                            </>
                          ) : overrideCount > 0 ? (
                            <>
                              <Bell className="size-3" />
                              {t("notif_settings.overrides", { n: overrideCount, count: overrideCount })}
                            </>
                          ) : (
                            <>{t("notif_settings.using_global")}</>
                          )}
                        </p>
                      </div>
                      <ChevronRight className="size-4 text-muted-foreground" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="devices" className="rounded-2xl border border-border bg-card overflow-hidden">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <div className="flex flex-col items-start gap-0.5 text-left">
              <span className="text-sm font-semibold">{t("notif_settings.acc_devices")}</span>
              <span className="text-[11px] font-normal text-muted-foreground">{t("notif_settings.acc_devices_sub", { n: activeDevices.length, count: activeDevices.length })}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 pt-0 space-y-6">
        {/* DEVICES */}
        <section>
          <SectionLabel>{t("notif_settings.sec_devices")}</SectionLabel>
          {devicesQ.isLoading ? (
            <div className="rounded-2xl border border-border bg-card p-4 text-xs text-muted-foreground">
              {t("notif_settings.loading_devices")}
            </div>
          ) : activeDevices.length + revokedDevices.length === 0 ? (
            <div className="rounded-2xl border border-border bg-card p-4 text-xs text-muted-foreground">
              {t("notif_settings.no_devices")}
            </div>
          ) : (
            <div className="space-y-2">
              {[...activeDevices, ...revokedDevices].map((d) => (
                <DeviceRow
                  key={d.id}
                  device={d}
                  isThisDevice={
                    !!thisLabel && d.device_label === thisLabel && !d.revoked_at
                  }
                  onRevoke={() => revokeMut.mutate(d.id)}
                  busy={revokeMut.isPending}
                />
              ))}
            </div>
          )}
        </section>

        {/* TEST PUSH */}
        <section>
          <SectionLabel>{t("notif_settings.sec_test_push")}</SectionLabel>
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-xl bg-accent grid place-items-center text-accent-foreground">
                <Send className="size-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{t("notif_settings.test_title")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("notif_settings.test_body")}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <Button
                size="sm"
                className="rounded-xl"
                disabled={testMut.isPending}
                onClick={() => testMut.mutate()}
              >
                {testMut.isPending && <Loader2 className="size-3.5 animate-spin mr-1" />}
                {t("notif_settings.btn_send_test")}
              </Button>
            </div>
          </div>
        </section>

        {/* LOCAL REMINDER */}
        <section>
          <SectionLabel>{t("notif_settings.sec_local_reminder")}</SectionLabel>
          <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-xl bg-accent grid place-items-center text-accent-foreground">
                <Clock className="size-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{t("notif_settings.reminder_title")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("notif_settings.reminder_body")}
                </p>
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {t("notif_settings.reminder_label")}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {REMINDER_DELAY_OPTIONS.map((opt) => {
                  const active = reminderDelay === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => pickReminderDelay(opt.value)}
                      className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${
                        active
                          ? "border-primary bg-primary/5"
                          : "border-border bg-card hover:bg-accent/30"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <div className="h-8" />

      {editingTrip && (
        <TripOverrideSheet
          open={!!editingTrip}
          onOpenChange={(v) => !v && setEditingTrip(null)}
          tripName={editingTrip.name}
          initialSettings={overridesByTrip.get(editingTrip.id)?.settings ?? {}}
          initialMutedUntil={overridesByTrip.get(editingTrip.id)?.muted_until ?? null}
          onSave={(settings, muted_until) =>
            tripSaveMut.mutate({ trip_id: editingTrip.id, settings, muted_until })
          }
          onClear={() => tripClearMut.mutate(editingTrip.id)}
        />
      )}
    </Screen>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  );
}

function DeviceRow({
  device,
  isThisDevice,
  onRevoke,
  busy,
}: {
  device: DeviceTokenRow;
  isThisDevice: boolean;
  onRevoke: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const revoked = !!device.revoked_at;
  const platformLabel = device.platform.toUpperCase();
  const dateLabel = new Date(device.last_seen_at).toLocaleDateString();
  return (
    <div
      className={`rounded-2xl border p-4 ${
        revoked ? "border-dashed border-border bg-muted/30 opacity-70" : "border-border bg-card"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="size-10 rounded-xl bg-accent grid place-items-center text-accent-foreground shrink-0">
          <Smartphone className="size-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-sm truncate">
              {device.device_label ?? t("notif_settings.device_fallback_label", { platform: device.platform })}
            </p>
            {isThisDevice && (
              <span className="rounded-full bg-primary/10 text-primary text-[10px] font-semibold px-2 py-0.5">
                {t("notif_settings.tag_this_device")}
              </span>
            )}
            {revoked && (
              <span className="rounded-full bg-muted text-muted-foreground text-[10px] font-semibold px-2 py-0.5">
                {t("notif_settings.tag_push_off")}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {device.app_version
              ? t("notif_settings.device_meta_with_ver", { platform: platformLabel, ver: device.app_version, date: dateLabel })
              : t("notif_settings.device_meta", { platform: platformLabel, date: dateLabel })}
          </p>
        </div>
        {!revoked && (
          <Button
            size="sm"
            variant="ghost"
            className="rounded-xl text-xs text-muted-foreground hover:text-destructive"
            disabled={busy}
            onClick={onRevoke}
          >
            <Trash2 className="size-3.5 mr-1" />
            {t("notif_settings.btn_disable_push")}
          </Button>
        )}
      </div>
    </div>
  );
}

